import { Icon, TextBalancer } from 'components/common';
import { AnimatePresence, motion } from 'framer-motion';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { useRef } from 'react';
import { PERK_LAYOUT_IDS } from './constants';
import { PerkBanner } from './PerkBanner';
import { PerkProgressButton } from './PerkProgressButton';
import { PerkValidity } from './PerkValidity';
import { Perk } from './types';
import { formatValidityDate, isUpcoming } from './utils';
import { isNil, isEmpty } from 'lib/javascript';
import { formatMarkdownToHtml } from 'utils';

export const PerkCardModal = ({
  perk,
  onClose,
  layoutIds,
  onUnlock,
  onRedeemClick,
}: {
  perk: Perk | null;
  onClose: () => void;
  layoutIds: typeof PERK_LAYOUT_IDS;
  onUnlock?: (perk: Perk) => Promise<void>;
  onRedeemClick?: (perk: Perk) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, onClose);

  return (
    <>
      <AnimatePresence>
        {perk && (
          <motion.div
            key={`overlay-${perk.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-bg-black/20 tw-h-full tw-w-full tw-z-[1050]"
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {perk ? (
          <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-[1060]">
            <motion.div
              key={`card-${perk.id}`}
              layoutId={`${layoutIds.card}-${perk.id}`}
              ref={ref}
              className="tw-flex tw-flex-col tw-w-full tw-max-w-[500px] tw-h-full md:tw-h-fit md:tw-max-h-[90%] tw-bg-white sm:tw-rounded-xl"
            >
              {/* Banner */}
              <div className="tw-relative tw-flex-shrink-0">
                <motion.div
                  layoutId={`${layoutIds.cardImage}-${perk.id}`}
                >
                  <PerkBanner
                    banners={perk.banners}
                    logo={perk.logo}
                  />
                </motion.div>
                <motion.div
                  layoutId={`${layoutIds.cardCta}-${perk.id}`}
                  className="tw-absolute tw-right-6 tw-top-6 tw-flex tw-items-center tw-cursor-pointer"
                >
                  <div
                    className="tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-full tw-h-10 tw-w-10 tw-cursor-pointer tw-shadow"
                    role="button"
                    onClick={onClose}
                  >
                    <Icon name="close" fontSize={14} />
                  </div>
                </motion.div>
              </div>

              {/* Scrollable content */}
              <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pt-4 tw-space-y-4 custom-scrollbar tw-overflow-x-hidden">
                <motion.div
                  layoutId={`${layoutIds.cardValidity}-${perk.id}`}
                  className="tw-pt-4"
                >
                  <PerkValidity
                    startedAt={formatValidityDate(perk.started_at)}
                    endedAt={formatValidityDate(perk.ended_at)}
                    quota={perk.quota}
                    totalRedeemed={perk.total_redeem}
                    isUpcoming={isUpcoming(perk.started_at)}
                  />
                </motion.div>
                <motion.div
                  layoutId={`${layoutIds.cardTitle}-${perk.id}`}
                  className="tw-flex tw-flex-col tw-gap-2"
                >
                  <h2 className="tw-text-center tw-font-extrabold tw-text-3xl tw-mb-0">
                    {perk.title}
                  </h2>
                  <p className="tw-mb-0 tw-leading-tight tw-text-2xl tw-text-center tw-text-[#333] tw-overflow-hidden">
                    {perk.excerpt}
                  </p>
                </motion.div>
                {perk.description && (
                  <TextBalancer
                    as="pre"
                    className="tw-text-center tw-px-4 tw-leading-snug tw-mt-2 !tw-mb-3 tw-font-[inherit] tw-text-[#777] tw-whitespace-pre-wrap"
                  >
                    {perk.description}
                  </TextBalancer>
                )}
                {perk.tnc && (
                  <div className="tw-flex tw-flex-col tw-gap-1 tw-justify-center tw-items-center">
                    <p className="tw-mb-0 tw-font-bold tw-text-[1.2rem]">
                      Terms & Conditions
                    </p>
                    <pre
                      className="tw-text-center tw-px-4 tw-leading-tight tw-font-[inherit] tw-text-[#777] tw-my-0 tw-text-[1.2rem] tw-whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{
                        __html: formatMarkdownToHtml(perk.tnc),
                      }}
                    />
                  </div>
                )}
                {(isUpcoming(perk.started_at) || !perk.progress) &&
                  perk.partner_name && (
                    <div className="tw-border-t tw-h-0 tw-mt-2.5 tw-mb-1" />
                  )}
              </div>

              {/* Button */}
              <div className="tw-flex-shrink-0 tw-flex tw-flex-col tw-sticky tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-bg-white">
                {!isUpcoming(perk.started_at) && (
                  <div className="tw-px-4">
                    <motion.div
                      className="tw-mt-4"
                      layoutId={`${layoutIds.cardProgress}-${perk.id}`}
                    >
                      <PerkProgressButton
                        perk={perk}
                        onUnlock={onUnlock}
                        onRedeemClick={onRedeemClick}
                      />
                    </motion.div>
                  </div>
                )}
                <motion.div
                  layoutId={`${layoutIds.cardPartner}-${perk.id}`}
                  className="tw-flex tw-items-center tw-justify-center tw-py-4"
                >
                  {!isNil(perk.partner_name) &&
                  !isEmpty(perk.partner_name) ? (
                    <span className="tw-text-[1.375rem] tw-text-white-[#777]">
                      Courtesy of{' '}
                      <strong>
                        {!isNil(perk.partner_name) &&
                        !isEmpty(perk.partner_name)
                          ? perk.partner_name
                          : 'Yummy Bros'}
                      </strong>
                    </span>
                  ) : null}
                </motion.div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
    </>
  );
};
